// @tennis-matches/services/governance — Ziel-Felder für Superadmin-Vereinsimport
//
// Nur öffentliche Impressum-Stammdaten des Vereins — keine personenbezogenen Beziehungsdaten.

/**
 * Erweiterbar: neue öffentliche Stammdaten hier ergänzen (key + aliases für Auto-Mapping).
 * @type {Array<{ key: string, labelKey: string, required?: boolean, aliases?: string[] }>}
 */
export const CLUB_IMPORT_FIELDS = [
  {
    key: "club_name",
    labelKey: "superadmin.clubs.fields.clubName",
    required: true,
    aliases: ["verein", "vereinsname", "club", "name", "club_name", "tennisverein", "organisation"],
  },
  {
    key: "club_short_name",
    labelKey: "superadmin.clubs.fields.shortName",
    aliases: ["kurzname", "short_name", "club_short_name", "abkürzung", "abkuerzung"],
  },
  {
    key: "legal_name",
    labelKey: "superadmin.clubs.fields.legalName",
    aliases: ["rechtlicher name", "legal_name", "eingetragener name", "vereinsname register", "firma"],
  },
  {
    key: "founded_year",
    labelKey: "superadmin.clubs.fields.foundedYear",
    aliases: ["gründung", "gruendung", "founded", "founded_year", "gründungsjahr", "gruendungsjahr", "jahr"],
  },
  {
    key: "street",
    labelKey: "superadmin.clubs.fields.street",
    aliases: ["straße", "strasse", "street", "str", "adresse"],
  },
  {
    key: "house_number",
    labelKey: "superadmin.clubs.fields.houseNumber",
    aliases: ["hausnummer", "hnr", "house_number", "nr", "haus-nr", "haus nr"],
  },
  {
    key: "postal_code",
    labelKey: "superadmin.clubs.fields.postalCode",
    aliases: ["plz", "postleitzahl", "postal_code", "zip", "postcode"],
  },
  {
    key: "city",
    labelKey: "superadmin.clubs.fields.city",
    aliases: ["ort", "stadt", "city", "gemeinde", "wohnort"],
  },
  {
    key: "country",
    labelKey: "superadmin.clubs.fields.country",
    aliases: ["land", "country", "staat", "nation"],
  },
  {
    key: "email",
    labelKey: "superadmin.clubs.fields.email",
    aliases: ["email", "e-mail", "mail", "kontakt-email", "vereins-email", "vereinsemail"],
  },
  {
    key: "phone",
    labelKey: "superadmin.clubs.fields.phone",
    aliases: ["telefon", "tel", "phone", "telefonnummer", "fon"],
  },
  {
    key: "website_url",
    labelKey: "superadmin.clubs.fields.website",
    aliases: ["webseite", "website", "url", "homepage", "internet", "website_url"],
  },
  {
    key: "register_court",
    labelKey: "superadmin.clubs.fields.registerCourt",
    aliases: ["registergericht", "register_court", "amtsgericht", "gericht"],
  },
  {
    key: "register_number",
    labelKey: "superadmin.clubs.fields.registerNumber",
    aliases: ["registernummer", "register_number", "vr nummer", "vr-nummer", "vereinsregister"],
  },
  {
    key: "dtb_club_id",
    labelKey: "superadmin.clubs.fields.dtbClubId",
    aliases: ["dtb", "dtb_id", "dtb_club_id", "dtb-nummer"],
  },
  {
    key: "btv_club_id",
    labelKey: "superadmin.clubs.fields.btvClubId",
    aliases: ["btv", "btv_id", "btv_club_id", "btv-nummer"],
  },
];

function __normHeader(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * @param {string[]} headers
 * @returns {Record<string, string>} fieldKey -> header label (original)
 */
export function suggestClubImportMapping(headers) {
  const list = Array.isArray(headers) ? headers.map((h) => String(h ?? "").trim()) : [];
  const mapping = {};
  const used = new Set();

  for (const field of CLUB_IMPORT_FIELDS) {
    const aliases = new Set(
      [field.key, ...(field.aliases || [])].map((a) => __normHeader(a)).filter(Boolean)
    );
    let match = "";
    for (const header of list) {
      if (!header || used.has(header)) continue;
      const norm = __normHeader(header);
      if (!norm) continue;
      if (aliases.has(norm)) {
        match = header;
        break;
      }
    }
    if (match) {
      mapping[field.key] = match;
      used.add(match);
    }
  }

  return mapping;
}

/**
 * @param {string[][]} rowsAoA
 * @param {Record<string, string>} mapping fieldKey -> excel header
 */
export function mapClubImportRows(rowsAoA, mapping) {
  const rows = Array.isArray(rowsAoA) ? rowsAoA : [];
  if (rows.length < 2) return [];

  const headers = (rows[0] || []).map((h) => String(h ?? "").trim());
  const headerIndex = new Map(headers.map((h, i) => [h, i]));
  const out = [];

  for (let r = 1; r < rows.length; r++) {
    const line = rows[r] || [];
    const record = {};
    let hasValue = false;

    for (const field of CLUB_IMPORT_FIELDS) {
      const header = mapping?.[field.key];
      if (!header) continue;
      const idx = headerIndex.get(header);
      if (idx == null) continue;
      const value = String(line[idx] ?? "").trim();
      if (!value) continue;
      record[field.key] = value;
      hasValue = true;
    }

    if (hasValue && __asStr(record.club_name)) {
      out.push(record);
    }
  }

  return out;
}

function __asStr(v) {
  return String(v ?? "").trim();
}
